import styled, { css } from 'styled-components';
import InputBase from './InputBase';
import HBIconButton from '../HBIconButton/HBIconButton';
import spacing from '../../styles/spacing';
import {
    textFieldBaseStyles,
    hbFieldStateBorder,
    getCustomThemeDSContainer,
    getCustomThemeDSInput
} from '../HBTextField/HBTextField.styles';
import { ICustomThemeInput } from '../HBTextField/HBTextField.types';
import {
    INPUT_SIZE_PROPS,
    TTextFieldSize,
    DS_FIELD_BORDER_RADIUS
} from '../HBTextField/variables';
import { vars } from '../../theme';

export function getInputBorderRadius(
    borderRadius: number,
    inputSize: TTextFieldSize
) {
    const inputHeight = parseInt(
        INPUT_SIZE_PROPS.builder[inputSize].height,
        10
    );
    const percentBorderRadius = borderRadius / 100;
    return percentBorderRadius * (inputHeight / 2);
}

/* Input extends from InputBase */
export const Input = styled(InputBase)`
    && {
        ${textFieldBaseStyles}
        ${props => INPUT_SIZE_PROPS[props.scope][props.size]}
        ${props =>
            props.readOnly &&
            css`
                pointer-events: none;
            `}

        + span {
            // this shouldn't exist!
            margin-top: ${spacing[8]};
        }

        ::-webkit-outer-spin-button,
        ::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }
    &[type='number'] {
        -moz-appearance: textfield;
    }
`;

// ─── DS Mode Styled Components ──────────────────────────────────────────

// FieldContainer owns the border, padding, and gap between prefix/content/suffix/clear.
// Fixed height always — adding tags never grows the container; ContentArea scrolls horizontally.
export const FieldContainer = styled.div<{
    $size: TTextFieldSize;
    $variant: 'outlined' | 'ghost';
    $hasTags?: boolean;
    $error?: boolean;
    $disabled?: boolean;
    $focused?: boolean;
    $customTheme?: ICustomThemeInput;
}>`
    display: flex;
    align-items: center;
    width: 100%;
    position: relative;
    height: ${({ $size }) => INPUT_SIZE_PROPS.system[$size]?.height ?? '42px'};
    overflow: clip;
    overflow-clip-margin: 3px;
    padding: 0 ${spacing[12]};
    gap: ${spacing[8]};
    border-radius: ${({ $size }) => DS_FIELD_BORDER_RADIUS[$size]};
    background: ${vars.colors.surface.item};
    cursor: text;
    box-sizing: border-box;
    transition: border-color 0.15s ease, background 0.15s ease,
        box-shadow 0.15s ease;

    ${({ $variant, $error, $disabled, $focused }) =>
        hbFieldStateBorder({ $variant, $error, $disabled, $focused })}

    ${({ $customTheme, $error, $disabled, $focused }) =>
        $customTheme &&
        getCustomThemeDSContainer($customTheme, $error, $disabled, $focused)}
`;

// No horizontal padding — FieldContainer's padding + gap handles all spacing.
// padding-top: 2px corrects the optical misalignment of text affixes with the input value;
// only applied when the affix content is text ($isText), not for icon SVGs.
export const AffixSlot = styled.div<{ $isText?: boolean }>`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    line-height: 0;
    ${({ $isText }) => $isText && `padding-top: ${spacing[2]};`}
    color: ${vars.colors.text.tertiary};

    svg {
        display: block;
    }
`;

export const ContentArea = styled.div<{ $hasTags?: boolean }>`
    display: flex;
    ${({ $hasTags }) => $hasTags && 'align-items: center;'}
    flex: 1;
    height: 100%;
    min-width: 0;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    gap: ${spacing[4]};
`;

// && raises specificity to (0,2,0) — beats the Storybook global angularStyles rule
// `input[type='text'] { border, background, height, margin, padding, box-shadow }`
// which sits at (0,1,1) and wins over a plain class selector.
export const DSInnerInput = styled.input<{
    $size: TTextFieldSize;
    $hasTags?: boolean;
    $customTheme?: ICustomThemeInput;
}>`
    && {
        all: unset;
        -webkit-appearance: none;
        appearance: none;
        display: block;
        margin: 0;
        padding: 0;
        height: ${({ $hasTags, $size }) =>
            $hasTags
                ? 'auto'
                : INPUT_SIZE_PROPS.system[$size]?.height ?? '42px'};
        border: none;
        border-radius: 0;
        box-shadow: none;
        outline: none;
        flex: 1;
        min-width: 60px; /* 60px not in spacing scale — minimum usable typing width */
        width: 100%;
        font-size: ${({ $size }) =>
            INPUT_SIZE_PROPS.system[$size]?.fontSize ?? '14px'};
        line-height: 1.5;
        color: ${vars.colors.text.primary};
        background: transparent;
        cursor: text;
    }

    &&:focus,
    &&:focus-visible {
        outline: none;
        box-shadow: none;
        border: none;
        background: transparent;
    }

    &::placeholder {
        color: ${vars.colors.text.tertiary};
    }

    &&:disabled {
        color: ${vars.colors.text.disabled};
        -webkit-text-fill-color: ${vars.colors.text.disabled};
        cursor: not-allowed;
    }

    ${({ $customTheme }) => $customTheme && getCustomThemeDSInput($customTheme)}
`;

// Overrides HBIconButton's theme color so the clear icon renders in text.tertiary.
// && raises specificity above the theme's (0,1,0) rule.
export const ClearIconButton = styled(HBIconButton)`
    &&,
    && * {
        color: ${vars.colors.text.tertiary};
    }

    &&:not(:has([data-hbui-preserve-fill])) svg * {
        fill: ${vars.colors.text.tertiary};
    }
`;
